/**
 * ServiceTimeService
 * Service for ServiceTime entity operations
 * Route: api/service-times
 * Controller: ServiceTimesController.cs
 *
 * Times are sent "HH:mm" and returned "HH:mm:ss". DELETE archives (soft delete); `restore` undoes it.
 */
import { CrudService } from '../base/CrudService';
/**
 * ServiceTimeService
 * Handles service time CRUD operations and custom endpoints
 * Maps to /api/service-times endpoint
 */
export class ServiceTimeService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ServiceTimeService.DEFAULT_SERVICE_PATH,
            basePath: ServiceTimeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get all (non-archived) service times for a specific location, by day then start time
     * GET /api/service-times/location/{locationId}
     * @param locationId - Location ID
     * @returns Array of service times
     */
    async getByLocationId(locationId) {
        const response = await this.getByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all service times for a specific location (raw)
     * @param locationId - Location ID
     * @returns ResponseData with array of service times
     */
    getByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('byLocationId', `location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Service times of a location on one day (0 = Sunday … 6 = Saturday; legacy null rows count as Sunday)
     * GET /api/service-times/location/{locationId}/day/{dayOfWeek}
     */
    async getByLocationAndDay(locationId, dayOfWeek) {
        const response = await this.getByLocationAndDayRaw(locationId, dayOfWeek);
        return response.data ?? [];
    }
    getByLocationAndDayRaw(locationId, dayOfWeek) {
        const endpoint = this.getEndpoint('byLocationAndDay', `location/${locationId}/day/${dayOfWeek}`);
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * Archived (soft-deleted) service times, newest archive first, for a restore list
     * GET /api/service-times/archived?locationId=
     * @param locationId - Optional location (0 is the Online Campus, a real location); omit for every location
     */
    async getArchived(locationId) {
        const response = await this.getArchivedRaw(locationId);
        return response.data ?? [];
    }
    getArchivedRaw(locationId) {
        const endpoint = this.getEndpoint('archived', 'archived');
        return this.http.get(this.buildUrlWithParams(endpoint, { locationId }));
    }
    /**
     * Archive a service time (soft delete) — same call as `delete`, returns the archived row
     * DELETE /api/service-times/{id}
     */
    async archive(id) {
        const response = await this.archiveRaw(id);
        if (!response.data) {
            throw new Error(`Failed to archive service time ${id}`);
        }
        return response.data;
    }
    archiveRaw(id) {
        const endpoint = this.getEndpoint('archive', `${id}`);
        return this.http.delete(this.buildUrl(endpoint));
    }
    /**
     * Restore an archived service time (it comes back inactive)
     * POST /api/service-times/{id}/restore
     */
    async restore(id) {
        const response = await this.restoreRaw(id);
        if (!response.data) {
            throw new Error(`Failed to restore service time ${id}`);
        }
        return response.data;
    }
    restoreRaw(id) {
        const endpoint = this.getEndpoint('restore', `${id}/restore`);
        return this.http.post(this.buildUrl(endpoint), {});
    }
}
ServiceTimeService.DEFAULT_SERVICE_PATH = 'locations';
ServiceTimeService.BASE_PATH = 'api/service-times';
//# sourceMappingURL=ServiceTimeService.js.map